/**
 * Build a bustimes.org-style timetable matrix (stops = rows, journeys = columns) from a
 * set of journeys, each of which is an ordered list of (stop, time) calls.
 *
 * The hard part is the **stop spine**: different journeys on the same route serve
 * different subsets of stops (short-workings, branches, express runs), so we merge their
 * ordered stop sequences into a single consistent row order — the same approach
 * bustimes / standard transit-timetable renderers use. A journey that skips a spine stop
 * simply leaves that cell blank.
 *
 * Times are GTFS "seconds since midnight" and may exceed 86400 for after-midnight calls
 * (preserved, never normalized — see `secondsToHHMM`). This module is pure: no React, no
 * timezone / wall-time arithmetic, fully unit-testable.
 */

/** One call at a stop within a journey. `seconds` is null when the stop is served but untimed. */
export interface JourneyCall {
  stopId: string;
  seconds: number | null;
}

/** A single journey = one column of the timetable. */
export interface InputJourney {
  id: string;
  headsign?: string | null;
  /** Used only to order the columns left→right. Null sorts last. */
  startSeconds: number | null;
  /** Stop calls in the order the journey serves them. */
  stops: JourneyCall[];
}

export interface MatrixStopRow {
  stopId: string;
  name: string;
  /**
   * A stop is a "timing point" if at least one journey publishes an explicit time there.
   * Untimed (interpolated) intermediate stops are hidden by the "Timing points" view —
   * mirroring bustimes. When a feed times every stop, every stop is a timing point and
   * the two views coincide (the toggle is then hidden by the UI).
   */
  isTimingPoint: boolean;
  lat?: number;
  lng?: number;
}

export interface MatrixJourneyCol {
  id: string;
  headsign?: string | null;
}

export interface TimetableMatrix {
  stops: MatrixStopRow[];
  journeys: MatrixJourneyCol[];
  /** times[rowIndex][colIndex] — seconds since midnight, or null if not served / untimed. */
  times: Array<Array<number | null>>;
  /** True when at least one stop is not a timing point (i.e. the toggle is meaningful). */
  hasNonTimingPoints: boolean;
}

/** Stop metadata resolver — supplies the display name and (optional) coordinates per stopId. */
export type ResolveStop = (stopId: string) => { name?: string | null; lat?: number; lng?: number } | undefined;

/**
 * Merge the ordered stop sequences of every journey into a single spine.
 *
 * Greedy ordered-merge: walk each journey keeping a write pointer into the spine. A stop
 * already in the spine at/after the pointer advances the pointer past it; a new stop is
 * inserted at the pointer. This preserves each journey's relative order while folding in
 * stops other journeys introduce, and is stable for the common case where every journey
 * shares one underlying stop order. Stops seen earlier than the pointer (loops/figure-of-
 * eights) are not re-inserted — they keep their first position.
 */
function buildSpine(journeys: InputJourney[]): string[] {
  const spine: string[] = [];
  const seen = new Set<string>();

  for (const journey of journeys) {
    let pointer = 0;
    for (const call of journey.stops) {
      const existingIdx = spine.indexOf(call.stopId, 0);
      if (existingIdx === -1) {
        spine.splice(pointer, 0, call.stopId);
        seen.add(call.stopId);
        pointer += 1;
      } else if (existingIdx >= pointer) {
        pointer = existingIdx + 1;
      }
      // existingIdx < pointer → the stop appears out of order for this journey (a loop);
      // leave it where it is and keep the pointer so we stay monotonic.
    }
  }

  return spine;
}

function compareStart(a: InputJourney, b: InputJourney): number {
  if (a.startSeconds == null && b.startSeconds == null) return 0;
  if (a.startSeconds == null) return 1;
  if (b.startSeconds == null) return -1;
  return a.startSeconds - b.startSeconds;
}

/**
 * Build the full timetable matrix. Columns are ordered by `startSeconds`; rows follow the
 * merged spine. `resolveStop` supplies names/coords; unknown stops fall back to their id.
 */
export function buildTimetableMatrix(journeys: InputJourney[], resolveStop: ResolveStop = () => undefined): TimetableMatrix {
  const ordered = [...journeys].sort(compareStart);
  const spine = buildSpine(ordered);
  const rowIndex = new Map<string, number>();
  spine.forEach((stopId, i) => rowIndex.set(stopId, i));

  const times: Array<Array<number | null>> = spine.map(() => ordered.map(() => null));
  const timed = new Set<string>();

  ordered.forEach((journey, col) => {
    for (const call of journey.stops) {
      const row = rowIndex.get(call.stopId);
      if (row == null) continue;
      times[row][col] = call.seconds;
      if (call.seconds != null) timed.add(call.stopId);
    }
  });

  const stops: MatrixStopRow[] = spine.map((stopId) => {
    const meta = resolveStop(stopId);
    return {
      stopId,
      name: meta?.name || stopId,
      isTimingPoint: timed.has(stopId),
      lat: meta?.lat,
      lng: meta?.lng,
    };
  });

  const journeyCols: MatrixJourneyCol[] = ordered.map((j) => ({ id: j.id, headsign: j.headsign ?? null }));
  const hasNonTimingPoints = stops.some((s) => !s.isTimingPoint);

  return { stops, journeys: journeyCols, times, hasNonTimingPoints };
}
